import React, { useCallback, useMemo } from "react";
import { SearchOption, TypeaheadInput, toast, ErrorMessage, Flex } from "@jsq/ds/core";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import { useGetTreasuryArenasLazyQuery } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";

export interface ClientSelectorProps {
  selectedArenaName?: string;
  onClientSelect: (arenaId: string, arenaName: string, arenaDomain?: string) => void;
  disabled?: boolean;
  placeholder?: string;
  showErrorToast?: boolean;
  error?: string;
}

type ArenaEdgeType = NonNullable<
  NonNullable<ReturnType<typeof useGetTreasuryArenasLazyQuery>[1]["data"]>["treasuryArenas"]
>["edges"][0];

/**
 * Shared ClientSelector component for selecting treasury arenas (clients).
 * Used by both PayeeWorkflows and PayableWorkflows.
 */
export const ClientSelector: React.FC<ClientSelectorProps> = ({
  selectedArenaName,
  onClientSelect,
  disabled = false,
  placeholder = "Select a client",
  showErrorToast = true,
  error,
}) => {
  const [fetchArenasQuery, { data, loading }] = useGetTreasuryArenasLazyQuery();

  // Extract arena list from GraphQL response
  const arenaOptions: SearchOption[] = useMemo(
    () =>
      data?.treasuryArenas?.edges.map((arena: ArenaEdgeType) => ({
        id: arena.node.id,
        label: arena.node.name,
      })) || [],
    [data],
  );

  // Create a lookup map for arena domains
  const arenaDomainMap = useMemo(() => {
    const map = new Map<string, string>();
    data?.treasuryArenas?.edges.forEach((arena: ArenaEdgeType) => {
      if (arena.node.domain) {
        map.set(arena.node.id, arena.node.domain);
      }
    });
    return map;
  }, [data]);

  // Handle search with debouncing and optional error handling
  const handleDebouncedSearch = useCallback(
    async (term: string) => {
      if (term.length < 1) {
        return;
      }

      try {
        const result = await fetchArenasQuery({
          context: clientContext(),
          variables: {
            searchTerm: term,
          },
          fetchPolicy: "cache-and-network",
        });
        if (result.error) {
          addError(formatError(result.error));
          if (showErrorToast) {
            toast.error("Failed to search for clients. Please try again.");
          }
        }
      } catch (err) {
        addError(formatError(err));
        if (showErrorToast) {
          toast.error("Failed to search for clients. Please try again.");
        }
      }
    },
    [fetchArenasQuery, showErrorToast],
  );

  // Handle client selection
  const handleClientSelect = useCallback(
    (selectedOption?: SearchOption) => {
      if (selectedOption) {
        const domain = arenaDomainMap.get(String(selectedOption.id));
        onClientSelect(String(selectedOption.id), selectedOption.label, domain);
      }
    },
    [onClientSelect, arenaDomainMap],
  );

  const displayError = useMemo(() => {
    if (error) {
      return error;
    }

    return null;
  }, [error]);

  return (
    <Flex flexDirection="column" gap={16}>
      <TypeaheadInput
        disabled={disabled}
        isClearable
        isFullWidth
        isLoading={loading}
        options={arenaOptions}
        onDebouncedSearch={handleDebouncedSearch}
        onSelectOption={handleClientSelect}
        placeholder={placeholder}
        minLength={1}
        initialSearchValue={selectedArenaName}
      />
      {displayError && <ErrorMessage>{displayError}</ErrorMessage>}
    </Flex>
  );
};
